import { useEffect, useState } from 'react';
import { useApi, useAuthedImage } from '../../lib/api';

type Block = {
  seat_block_id: string;
  seller_id: string;
  section: string;
  row: string;
  seats_json: string;
  seat_count: number;
  section_type: string;
  verified: number;
  verification_photo_url: string | null;
  seat_view_photo_url: string | null;
  notes: string | null;
  email: string;
  full_name: string | null;
};

export default function AdminSeatBlocks() {
  const api = useApi();
  const [filter, setFilter] = useState<'0' | '1'>('0');
  const [blocks, setBlocks] = useState<Block[]>([]);
  const [note, setNote] = useState<Record<string, string>>({});
  const [busy, setBusy] = useState<string | null>(null);

  async function refresh() {
    const { seat_blocks } = await api<{ seat_blocks: Block[] }>(
      `/admin/seat-blocks?verified=${filter}`
    );
    setBlocks(seat_blocks);
  }
  useEffect(() => {
    refresh().catch(console.error);
  }, [filter]);

  async function decide(id: string, action: 'verify' | 'reject') {
    setBusy(id + action);
    try {
      await api(`/admin/seat-blocks/${id}`, {
        method: 'PATCH',
        body: JSON.stringify({ action, notes: note[id] ?? '' }),
      });
      await refresh();
    } finally {
      setBusy(null);
    }
  }

  return (
    <section>
      <h2>Seat verification</h2>
      <div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1rem' }}>
        <button
          className={filter === '0' ? 'btn' : 'btn-secondary'}
          onClick={() => setFilter('0')}
        >
          Pending
        </button>
        <button
          className={filter === '1' ? 'btn' : 'btn-secondary'}
          onClick={() => setFilter('1')}
        >
          Verified
        </button>
      </div>

      {blocks.length === 0 && (
        <p className="muted">No {filter === '0' ? 'pending' : 'verified'} seat blocks.</p>
      )}

      <div className="grid cols-3">
        {blocks.map((b) => {
          const seats = JSON.parse(b.seats_json) as string[];
          return (
            <div className="card" key={b.seat_block_id}>
              <p>
                <strong>Section {b.section}</strong> · Row {b.row}
              </p>
              <p className="muted">
                Seats {seats.join(', ')} · {b.section_type}
              </p>
              <p className="muted">
                {b.full_name ?? '(no name)'} · {b.email}
              </p>

              <VerificationPhoto seatBlockId={b.seat_block_id} hasPhoto={!!b.verification_photo_url} />

              {filter === '0' ? (
                <>
                  <textarea
                    placeholder="Internal note (optional)"
                    value={note[b.seat_block_id] ?? ''}
                    onChange={(e) => setNote({ ...note, [b.seat_block_id]: e.target.value })}
                  />
                  <div style={{ display: 'flex', gap: '0.5rem', marginTop: '0.5rem' }}>
                    <button
                      disabled={!b.verification_photo_url || busy === b.seat_block_id + 'verify'}
                      onClick={() => decide(b.seat_block_id, 'verify')}
                    >
                      Verify
                    </button>
                    <button
                      className="btn-secondary"
                      disabled={busy === b.seat_block_id + 'reject'}
                      onClick={() => decide(b.seat_block_id, 'reject')}
                    >
                      Reject
                    </button>
                  </div>
                  {!b.verification_photo_url && (
                    <p className="muted" style={{ fontSize: '0.8rem' }}>
                      Awaiting the seller's verification photo.
                    </p>
                  )}
                </>
              ) : (
                <>
                  <p>
                    <span className="badge">verified</span>
                  </p>
                  {b.notes && <p className="muted">Note: {b.notes}</p>}
                  <button className="btn-secondary" onClick={() => decide(b.seat_block_id, 'reject')}>
                    Revoke verification
                  </button>
                </>
              )}
            </div>
          );
        })}
      </div>
    </section>
  );
}

/** Loads a private verification photo via the authenticated media endpoint. */
function VerificationPhoto({ seatBlockId, hasPhoto }: { seatBlockId: string; hasPhoto: boolean }) {
  const loadImage = useAuthedImage();
  const [url, setUrl] = useState<string | null>(null);
  const [err, setErr] = useState(false);

  useEffect(() => {
    if (!hasPhoto) return;
    let objectUrl: string | null = null;
    loadImage(`/media/verification/${seatBlockId}`)
      .then((u) => {
        objectUrl = u;
        setUrl(u);
      })
      .catch(() => setErr(true));
    return () => {
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [seatBlockId, hasPhoto]);

  if (!hasPhoto) return null;
  if (err) return <p className="muted">Could not load verification photo.</p>;
  if (!url) return <p className="muted">Loading photo…</p>;
  return (
    <a href={url} target="_blank" rel="noreferrer">
      <img
        src={url}
        alt="Verification"
        style={{ width: '100%', borderRadius: 6, margin: '0.5rem 0', border: '1px solid #e5e7eb' }}
      />
    </a>
  );
}
